import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import { RewardData, CouponData } from "../../type";
import CustomAxios from "../../utils/CustomAxios";
import { useCustomerInfoContext } from "../../context/CustomerInfoContext";
import main from "../../assets/scss/main.module.scss";

export const ChangeModal = ({
  isPointSpendModalOpen,
  setIsPointSpendModalOpen,
  selectReward,
  getUsedRewardData,
  rallyPoint,
}: {
  isPointSpendModalOpen: boolean;
  setIsPointSpendModalOpen: (isPointSpendModalOpen: boolean) => void;
  selectReward: RewardData | CouponData | undefined;
  getUsedRewardData?: () => void;
  rallyPoint?: number;
}) => {
  const { getCustomerInfo, pointData } = useCustomerInfoContext();
  const [isSendLoading, setIsSendLoading] = useState<boolean>(false);
  const [isUseConfirm, setIsUseConfirm] = useState<boolean>(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] =
    useState<boolean>(false);

  // スタンプラリーポイントがある場合はラリーポイントを使用
  const point = rallyPoint ?? pointData;

  useEffect(() => {
    if (isPointSpendModalOpen && selectReward && selectReward?.description) {
      setIsDescriptionModalOpen(true);
    }
  }, [isPointSpendModalOpen, selectReward]);

  const onSend = () => {
    setIsSendLoading(true);

    const url = rallyPoint
      ? `/customer/reward/${selectReward?.uuid}/rally`
      : `/customer/reward/${selectReward?.uuid}`;
    CustomAxios.post(url, {
      isUsed: true,
    })
      .then(() => {
        getCustomerInfo?.();
        setIsConfirmModalOpen(true);
        getUsedRewardData?.();
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsPointSpendModalOpen(false);
        setIsUseConfirm(false);
        setIsSendLoading(false);
        window.location.reload();
      });
  };

  const handleOpenPointSpendModal = () => {
    setIsDescriptionModalOpen(false);
    setIsPointSpendModalOpen(true);
  };

  return (
    <>
      {isDescriptionModalOpen ? (
        <Modal open={isDescriptionModalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              width: "90%",
              height: "90%",
              maxWidth: "none",
              maxHeight: "none",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Paper elevation={0} sx={{ mx: 1 }}>
              <Typography
                id="modal-modal-title"
                variant="h6"
                component="h2"
                color="primary"
                sx={{
                  textAlign: "left",
                  my: 1,
                }}
              >
                景品の説明
              </Typography>
              <Typography variant="h5">
                {selectReward?.name} {selectReward?.point}pt
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  maxHeight: "70vh",
                  overflow: "auto",
                }}
              >
                <Typography>{selectReward?.description}</Typography>
              </Box>
            </Paper>
            <Paper elevation={0} sx={{ m: 2, mt: "auto" }}>
              <Button
                color="primary"
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => {
                  setIsDescriptionModalOpen(false);
                  setIsPointSpendModalOpen(false);
                }}
                sx={{ mb: 2 }}
              >
                閉じる
              </Button>
              <Button
                color="primary"
                variant="contained"
                size="large"
                fullWidth
                onClick={handleOpenPointSpendModal}
              >
                次へ
              </Button>
            </Paper>
          </Box>
        </Modal>
      ) : (
        <Modal open={isPointSpendModalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "auto",
              bottom: "5%",
              left: "50%",
              transform: "translateX(-50%)",
              bgcolor: "background.paper",
              width: "90%",
              height: "auto",
              maxWidth: "none",
              maxHeight: "none",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Paper elevation={0} sx={{ mx: 1 }}>
              <Typography
                id="modal-modal-title"
                variant="h6"
                component="h2"
                color="primary"
                sx={{
                  textAlign: "left",
                  my: 1,
                }}
              >
                以下の景品を交換しますか？
              </Typography>
              <Typography variant="h5">
                {selectReward?.name} {selectReward?.point}pt
              </Typography>
              <Typography sx={{ mt: 2, color: main.colorDarkGreen }}>
                所有ポイント {point.toLocaleString()}pt
              </Typography>
              {point >= Number(selectReward?.point) && (
                <>
                  <Typography sx={{ my: 2 }}>
                    景品を使用する際は、この画面を係りの者にお見せください。
                  </Typography>
                  <FormControlLabel
                    label="確認しました"
                    control={
                      <Checkbox
                        onChange={(e) => setIsUseConfirm(e.target.checked)}
                      />
                    }
                  />
                </>
              )}
              {point < Number(selectReward?.point) && (
                <Typography sx={{ my: 2, color: "red" }}>
                  ポイントが不足しています。
                </Typography>
              )}
            </Paper>

            {isSendLoading && (
              <Box sx={{ textAlign: "center", m: 2 }}>
                <CircularProgress />
              </Box>
            )}
            {!isSendLoading && (
              <Paper elevation={0} sx={{ m: 2 }}>
                <Button
                  color="primary"
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={() => {
                    setIsPointSpendModalOpen(false);
                  }}
                  sx={{ mb: 2 }}
                >
                  戻る
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={onSend}
                  disabled={!isUseConfirm}
                >
                  使う
                </Button>
              </Paper>
            )}
          </Box>
        </Modal>
      )}
      <Modal open={isConfirmModalOpen}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            width: "80%",
          }}
        >
          <Paper elevation={0} sx={{ mx: 2 }}>
            <Typography
              id="modal-modal-title"
              variant="h6"
              component="h2"
              color="primary"
              sx={{
                textAlign: "left",
                my: 1,
              }}
            >
              「{selectReward?.name}」を使用しました。
            </Typography>
          </Paper>
          <Paper elevation={0} sx={{ m: 2 }}>
            <Button
              color="primary"
              variant="outlined"
              size="large"
              fullWidth
              onClick={() => setIsConfirmModalOpen(false)}
            >
              閉じる
            </Button>
          </Paper>
        </Box>
      </Modal>
    </>
  );
};
